import React, { useMemo, useEffect } from "react";
import styled from "@emotion/styled";

import { QuestionWithRelations } from "../../../../../api/src/types";
import { AnswersDisputeGetResponse } from "../../../../../api/src/users/answers/route";

import { useGetQuestionnaireSectionGroupsByDocumentIdQuery } from "../../../api/questionnaire";
import CloseIcon from "../../../components/icons/CloseIcon";

import {
  ParagraphHeader,
  ParagraphSmall,
} from "../../../components/typography/Paragraph";
import {
  ModalContainer,
  ModalHeader,
  ModalContent,
  StyledCard,
  StyledCloseButton,
  StyledOverlay,
} from "./DisputeComponents";
import { QuestionContent } from "./QuestionContent";
import { PrimaryButton } from "../../../components/buttons/PrimaryButton";

import { COLOR_WARNING_TEXT, SPACING } from "../../../components/theme";
import useIsMobile from "../../../hooks/useIsMobile";

const InlineWarningText = styled(ParagraphSmall)`
  color: ${COLOR_WARNING_TEXT};
  margin-top: ${SPACING.SM};
`;

const MobileFooter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: relative;
  margin-top: ${SPACING.LG};
`;

export interface MinimalDispute {
  documentId: string;
  question: Pick<
    AnswersDisputeGetResponse[number]["question"],
    "id" | "parentQuestionGroupId" | "parentQuestionnaireSectionId"
  >;
}

interface ChangeAnswerModalProps {
  onClose: () => void;
  dispute: MinimalDispute;
  title?: string;
}

export const ChangeAnswerModal: React.FC<ChangeAnswerModalProps> = ({
  onClose,
  title = "Change Your Answer",
  dispute,
}) => {
  const {
    data: groups,
    error,
    isLoading,
  } = useGetQuestionnaireSectionGroupsByDocumentIdQuery({
    docId: dispute.documentId,
    section: dispute.question.parentQuestionnaireSectionId,
  });

  const isMobile = useIsMobile();

  const questionGroup = groups?.find(
    (group) => group.id === dispute.question.parentQuestionGroupId,
  );

  const question: QuestionWithRelations | undefined =
    questionGroup?.questions.find((q) => q.id === dispute.question.id);

  // Check immediate child question completion status
  const childQuestionsStatus = useMemo(() => {
    if (!question || !groups) {
      return {
        isParentQuestion: false,
        canClose: true,
        completedChildren: 0,
        totalChildren: 0,
      };
    }

    // Check if this question has immediate child questions
    const isParentQuestion = question.parentConditionalVisibilities.some(
      (cv) => cv.parentQuestionId === question.id,
    );

    if (!isParentQuestion || question.answers.length === 0) {
      return {
        isParentQuestion,
        canClose: true,
        completedChildren: 0,
        totalChildren: 0,
      };
    }

    // Get selected option IDs from current answer
    const selectedOptionIds = new Set(
      question.answers[0]?.answerQuestionOptions?.map(
        (aqo) => aqo.questionOptionId,
      ) ?? [],
    );

    // Find immediate child questions that should be visible
    const visibleChildQuestionIds = new Set<string>();
    for (const cv of question.parentConditionalVisibilities) {
      if (
        cv.parentQuestionId === question.id &&
        selectedOptionIds.has(cv.questionOptionId)
      ) {
        visibleChildQuestionIds.add(cv.childQuestionId);
      }
    }

    if (visibleChildQuestionIds.size === 0) {
      return {
        isParentQuestion: true,
        canClose: true,
        completedChildren: 0,
        totalChildren: 0,
      };
    }

    // Find child questions in groups data and check completion
    const allQuestions = groups.flatMap((group) => group.questions);
    const accessibleChildQuestions: QuestionWithRelations[] = [];
    let completedChildren = 0;

    for (const childId of visibleChildQuestionIds) {
      const childQuestion = allQuestions.find((q) => q.id === childId);
      if (childQuestion) {
        accessibleChildQuestions.push(childQuestion);
        if (childQuestion.answers.length > 0) {
          completedChildren++;
        }
      }
    }

    const totalChildren = accessibleChildQuestions.length;
    const canClose = completedChildren === totalChildren;

    return {
      isParentQuestion: true,
      canClose,
      completedChildren,
      totalChildren,
    };
  }, [question, groups]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && childQuestionsStatus.canClose) {
      onClose();
    }
  };

  const handleCloseClick = () => {
    if (childQuestionsStatus.canClose) {
      onClose();
    }
  };

  // Handle ESC key press
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && childQuestionsStatus.canClose) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [childQuestionsStatus.canClose, onClose]);

  return (
    <StyledOverlay
      onClick={handleOverlayClick}
      data-testid="change-answer-modal"
    >
      <ModalContainer>
        <StyledCard>
          <ModalHeader>
            <div style={{ flex: 1 }}>
              <ParagraphHeader fontWeight={700}>{title}</ParagraphHeader>
              {childQuestionsStatus.isParentQuestion &&
                !childQuestionsStatus.canClose && (
                  <InlineWarningText>
                    Answer all follow-up questions to continue (
                    {childQuestionsStatus.completedChildren}/
                    {childQuestionsStatus.totalChildren} completed)
                  </InlineWarningText>
                )}
            </div>
            <StyledCloseButton
              onClick={handleCloseClick}
              style={{
                opacity: childQuestionsStatus.canClose ? 1 : 0.5,
                cursor: childQuestionsStatus.canClose
                  ? "pointer"
                  : "not-allowed",
              }}
              title={
                childQuestionsStatus.canClose
                  ? "Close"
                  : "Complete all follow-up questions to close"
              }
            >
              <CloseIcon />
            </StyledCloseButton>
          </ModalHeader>

          <ModalContent>
            <QuestionContent
              isLoading={isLoading}
              error={error}
              question={question}
              groups={groups}
            />
          </ModalContent>

          {isMobile && (
            <MobileFooter>
              <PrimaryButton
                onClick={handleCloseClick}
                disabled={!childQuestionsStatus.canClose}
              >
                Save and continue
              </PrimaryButton>
            </MobileFooter>
          )}
        </StyledCard>
      </ModalContainer>
    </StyledOverlay>
  );
};
